'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { CardWrapper, Typography } from '../../../components';
import { FiltersSection, StatisticsTable, StatisticsChart } from './components';
import { StatisticsData, StatisticsFilters, Model } from './types';
import { transformStatisticsData } from './utils';
import { useTranslations } from '../../../contexts/translation.context';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import {
  Card,
  Chip,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Tab,
  Tabs,
  Spacer,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import clientStatisticService, { StatisticsResponse } from "../../../api/client/clientStatisticService";
import clientAccService from "../../../api/client/clientAccService";
import { usePageBreadcrumbs } from "../../../hooks/usePageBreadcrumbs";
import { useAuth } from "../../../contexts/AuthContext";

// Helper function to get date range based on filter
const getDateRange = (dateRange: string, createdUser:Date, customStartDate?: string, customEndDate?: string) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // If custom dates are provided, use them
  if (dateRange === 'custom' && customStartDate && customEndDate) {
    return {
      startDate: customStartDate,
      endDate: customEndDate,
    };
  }

  switch (dateRange) {
    case 'today':
      return {
        startDate: today.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'week':
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 7);
      return {
        startDate: weekStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'month':
      const monthStart = new Date(today);
      monthStart.setDate(today.getDate() - 30);
      return {
        startDate: monthStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'quarter': // 3 месяца
      const quarterStart = new Date(today);
      quarterStart.setDate(today.getDate() - 90);
      return {
        startDate: quarterStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'halfyear': // 6 месяцев
      const halfYearStart = new Date(today);
      halfYearStart.setDate(today.getDate() - 180);
      return {
        startDate: halfYearStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'year':
      const yearStart = new Date(today);
      yearStart.setDate(today.getDate() - 365);
      return {
        startDate: yearStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    case 'custom':
      // If custom is selected but no dates provided, fall back to default
      if (customStartDate && customEndDate) {
        return {
          startDate: customStartDate,
          endDate: customEndDate,
        };
      }
      // Fall through to default
      break;
    case 'all':
      // For "all time", return a very wide range or null to let API handle it
      const allTimeStart = new Date(createdUser); // Начало эпохи для этого приложения
      return {
        startDate: allTimeStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
    default:
      // Default to last 30 days if no range specified
      const defaultStart = new Date(today);
      defaultStart.setDate(today.getDate() - 30);
      return {
        startDate: defaultStart.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
      };
  }
};

// Helper function to combine subscribers and transactions data
const combineStatisticsData = (
  subscribersResponse: StatisticsResponse,
  transactionsResponse: StatisticsResponse,
  grouping: string = 'day'
): StatisticsData[] => {
  // Helper function to create a unique key for different grouping types
  const createKey = (item: any) => {
    if (grouping === 'week') {
      return `${item.year}-W${item.week.toString().padStart(2, '0')}`;
    } else if (grouping === 'month') {
      return item.month;
    } else {
      return item.date;
    }
  };

  // Helper function to format period for display
  const formatPeriod = (key: string) => {
    if (grouping === 'week') {
      const [year, week] = key.split('-W');
      return `${year} W${week}`;
    } else if (grouping === 'month') {
      const [year, month] = key.split('-');
      const monthNames = [
        'Янв',
        'Фев',
        'Мар',
        'Апр',
        'Май',
        'Июн',
        'Июл',
        'Авг',
        'Сен',
        'Окт',
        'Ноя',
        'Дек',
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    } else {
      return new Date(key).toLocaleDateString('ru-RU');
    }
  };
  const subscribersMap = new Map(
    subscribersResponse.objects.map((item) => [createKey(item), item.count || 0])
  );
  const transactionsMap = new Map(
    transactionsResponse.objects.map((item) => [
      createKey(item),
      parseFloat(item.amount?.toString() || item.count?.toString() || '0'),
    ])
  );

  const allKeys = new Set([
    ...subscribersResponse.objects.map((item) => createKey(item)),
    ...transactionsResponse.objects.map((item) => createKey(item)),
  ]);

  const sortedKeys = Array.from(allKeys).sort((a, b) => {
    if (grouping === 'week') {
      const [yearA, weekA] = a.split('-W');
      const [yearB, weekB] = b.split('-W');
      return yearA === yearB
        ? parseInt(weekA) - parseInt(weekB)
        : parseInt(yearA) - parseInt(yearB);
    } else if (grouping === 'month') {
      return a.localeCompare(b);
    } else {
      return new Date(a).getTime() - new Date(b).getTime();
    }
  });

  return sortedKeys.map((key, index) => {
    const subscribersCount = subscribersMap.get(key) || 0;
    const incomeAmount = transactionsMap.get(key) || 0;

    // Calculate changes compared to previous period (previous = older date, which is at index - 1 in ascending order)
    const prevSubscribers = index > 0 ? subscribersMap.get(sortedKeys[index - 1]) || 0 : 0;
    const prevIncome = index > 0 ? transactionsMap.get(sortedKeys[index - 1]) || 0 : 0;

    const subscribersChange =
      prevSubscribers > 0
        ? (((subscribersCount - prevSubscribers) / prevSubscribers) * 100).toFixed(0) + '%'
        : subscribersCount > 0
          ? '+100%'
          : '0%';
    const incomeChange =
      prevIncome > 0
        ? (((incomeAmount - prevIncome) / prevIncome) * 100).toFixed(0) + '%'
        : incomeAmount > 0
          ? '+100%'
          : '0%';

    return {
      id: key,
      period: formatPeriod(key),
      subscribersCount,
      subscribersChange,
      subscribersChangeType: subscribersCount >= prevSubscribers ? 'positive' : 'negative',
      incomeAmount,
      incomeChange,
      incomeChangeType: incomeAmount >= prevIncome ? 'positive' : 'negative',
    };
  });
};

const getIsoWeekStartDate = (year: number, week: number) => {
  if (!year || !week) return null;
  const simple = new Date(year, 0, 4); // Jan 4th is always in the first ISO week
  const dayOfWeek = simple.getDay() || 7; // Sunday -> 7
  simple.setDate(simple.getDate() - (dayOfWeek - 1)); // Move to Monday of first ISO week
  simple.setDate(simple.getDate() + (week - 1) * 7);
  return new Date(simple);
};

const formatRuFullDate = (date: Date) =>
  date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatRuShortDate = (date: Date) =>
  date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const getWeekRangeLabel = (id: string) => {
  const match = id.match(/^(\d{4})-W(\d{1,2})$/);
  if (!match) return '';

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);
  const startDate = getIsoWeekStartDate(year, week);

  if (!startDate) return '';

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  return `${formatRuFullDate(startDate)} - ${formatRuFullDate(endDate)}`;
};

const getMonthLabelWithYear = (id: string) => {
  const [yearStr, monthStr] = id.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1;

  if (Number.isNaN(year) || Number.isNaN(month)) return '';

  const date = new Date(year, month, 1);
  return date.toLocaleDateString('ru-RU', {
    month: 'short',
    year: 'numeric',
  });
};

const getDayLabelWithYear = (dateStr: string) => {
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? '' : formatRuShortDate(date);
};

// Chart configuration
const chartConfig = [
  {
    key: 'subscribers',
    title: 'Подписчики',
    suffix: 'подписчиков',
    type: 'number',
  },
  {
    key: 'income',
    title: 'Доход',
    suffix: 'доходов',
    type: 'currency',
  },
];

const Statistics: React.FC = () => {
  const { t } = useTranslations();
  const {user} = useAuth()
  const [statistics, setStatistics] = useState<StatisticsData[]>([]);
  const [chartStatistics, setChartStatistics] = useState<StatisticsData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/' },
      { label: t('sidebar.statistics'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  const [filters, setFilters] = useState<StatisticsFilters>(DEFAULT_FILTERS);
  const [activeChart, setActiveChart] = useState<string>(chartConfig[0].key);

  // Model selection state
  const [availableModels, setAvailableModels] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  const translatedChartData = useMemo(
    () =>
      chartConfig.map((item) => ({
        ...item,
        title:
          item.key === 'subscribers' ? t('dashboard.subscribersTitle') : t('dashboard.incomeTitle'),
        suffix:
          item.key === 'subscribers'
            ? t('dashboard.subscribersSuffix')
            : t('dashboard.incomeSuffix'),
      })),
    [t]
  );

  // Local states for debounced inputs
  const [localSearch, setLocalSearch] = useState(filters.search);

  const loadModels = useCallback(async (searchTerm: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: 50,
        search: searchTerm.trim() || undefined,
      });

      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const models = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableModels(models);
      } else {
        setAvailableModels([]);
      }
    } catch (error) {
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!modelSearchTerm.trim()) {
      loadModels();
      return;
    }

    const timer = setTimeout(() => {
      loadModels(modelSearchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [modelSearchTerm, loadModels]);

  // Debounce for search input
  useEffect(() => {
    if (localSearch === filters.search) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch, filters.search]);

  // Load statistics when dependencies change
  useEffect(() => {
    const loadStatistics = async () => {
      setIsLoading(true);
      try {
        const dateRange:any = getDateRange(filters.dateRange, user?.created_at, filters.customStartDate || undefined, filters.customEndDate || undefined);
        
        const apiParams = {
          page: currentPage,
          page_size: pageSize,
          sort_by: filters.sortBy || '-created_at',
          start_date: dateRange.startDate, // Always required
          end_date: dateRange.endDate, // Always required
          of_model_id: filters.models.length > 0 ? parseInt(filters.models[0]) : undefined,
          company_id: filters.companyId,
        };

        // Choose API methods based on grouping filter
        const getApiMethods = (grouping: string) => {
          switch (grouping) {
            case 'week':
              return {
                subscribers: () =>
                  clientStatisticService.getSubscribersStats({ ...apiParams, grouping: 'week' }),
                transactions: () =>
                  clientStatisticService.getTransactionsStats({ ...apiParams, grouping: 'week' }),
              };
            case 'month':
              return {
                subscribers: () =>
                  clientStatisticService.getSubscribersStats({ ...apiParams, grouping: 'month' }),
                transactions: () =>
                  clientStatisticService.getTransactionsStats({ ...apiParams, grouping: 'month' }),
              };
            default: // 'day'
              return {
                subscribers: () => clientStatisticService.getSubscribersStats(apiParams),
                transactions: () => clientStatisticService.getTransactionsStats(apiParams),
              };
          }
        };
        const apiMethods = getApiMethods(filters.grouping);
        const [subscribersResponse, transactionsResponse] = await Promise.all([
          apiMethods.subscribers(),
          apiMethods.transactions(),
        ]);
        const subscribersData = subscribersResponse?.data;
        const transactionsData = transactionsResponse?.data;
        const combinedData = combineStatisticsData(
          subscribersData,
          transactionsData,
          filters.grouping
        );
        setStatistics(combinedData);
        setTotalCount(Math.max(subscribersData.count, transactionsData.count));
      } catch (error) {
        console.error('Error loading statistics:', error);
        setStatistics([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadStatistics();
  }, [currentPage, pageSize, filters]);

  // Load chart data separately with large page size to get all data points
  useEffect(() => {
    const loadChartData = async () => {
      try {
        const dateRange: any = getDateRange(filters.dateRange, user?.created_at, filters.customStartDate || undefined, filters.customEndDate || undefined);

        const chartApiParams = {
          page: 1,
          page_size: 10000,
          sort_by: filters.sortBy || '-created_at',
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          of_model_id: filters.models.length > 0 ? parseInt(filters.models[0]) : undefined,
          company_id: filters.companyId,
        };

        const getChartApiMethods = (grouping: string) => {
          switch (grouping) {
            case 'week':
              return {
                subscribers: () => clientStatisticService.getSubscribersStats({ ...chartApiParams, grouping: 'week' }),
                transactions: () => clientStatisticService.getTransactionsStats({ ...chartApiParams, grouping: 'week' })
              };
            case 'month':
              return {
                subscribers: () => clientStatisticService.getSubscribersStats({ ...chartApiParams, grouping: 'month' }),
                transactions: () => clientStatisticService.getTransactionsStats({ ...chartApiParams, grouping: 'month' })
              };
            default:
              return {
                subscribers: () => clientStatisticService.getSubscribersStats(chartApiParams),
                transactions: () => clientStatisticService.getTransactionsStats(chartApiParams)
              };
          }
        };

        const chartMethods = getChartApiMethods(filters.grouping);
        const [subsRes, txRes] = await Promise.all([
          chartMethods.subscribers(),
          chartMethods.transactions()
        ]);

        const combinedChartData = combineStatisticsData(subsRes?.data, txRes?.data, filters.grouping);
        setChartStatistics(combinedChartData);
      } catch (error) {
        console.error('Error loading chart data:', error);
        setChartStatistics([]);
      }
    };

    loadChartData();
  }, [filters]);

  const activeChartData = useMemo(() => {
    const chart = translatedChartData.find((d) => d.key === activeChart);

    return {
      color: activeChart === 'subscribers' ? 'success' : 'danger',
      suffix: chart?.suffix || '',
      type: chart?.type || 'line',
    };
  }, [activeChart, translatedChartData]);

  // Transform chart statistics data for chart
  const chartData = useMemo(() => {
    if (!chartStatistics.length) return [];

    return chartStatistics.map(stat => {
      // Format label based on grouping type
      let label = '';
      let tooltipLabel = '';
      if (filters.grouping === 'week') {
        const [, week] = stat.id.split('-W');
        label = week ? `W${week}` : stat.id;
        tooltipLabel = getWeekRangeLabel(stat.id) || label;
      } else if (filters.grouping === 'month') {
        const [, monthNum] = stat.id.split('-');
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const monthIndex = parseInt(monthNum, 10) - 1;
        label = monthIndex >= 0 ? monthNames[monthIndex] || stat.id : stat.id;
        tooltipLabel = getMonthLabelWithYear(stat.id) || label;
      } else {
        // Для дней передаём полную дату
        label = stat.id; // Это будет дата в формате YYYY-MM-DD
        tooltipLabel = getDayLabelWithYear(stat.id) || label;
      }

      return {
        month: label,
        subscribers: stat.subscribersCount,
        income: stat.incomeAmount,
        tooltipLabel: tooltipLabel || label,
      };
    });
  }, [chartStatistics, filters.grouping]);

  const handleFilterChange = useCallback(
    (key: keyof StatisticsFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleModelSearchChange = useCallback((value: string) => {
    setModelSearchTerm(value);
  }, []);

  const formatValue = (value: number, type: string | undefined) => {
    if (type === 'number') {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(0) + 'k';
      }
      return value.toLocaleString();
    }
    if (type === 'currency') return `$${value.toFixed(2)}`;
    if (type === 'percentage') return `${value}%`;

    return value;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('dashboard.statistics')}
        </Typography>
      </div>

      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        availableModels={availableModels}
        modelsLoading={modelsLoading}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onModelSearchChange={handleModelSearchChange}
      />

      {/* Charts Section */}
      <div className="px-4 md:px-6 mb-6">
        <CardWrapper className="px-0 py-4 md:py-12">
          <StatisticsChart
            chartData={chartData}
            color={activeChartData.color}
            suffix={activeChartData.suffix}
            type={activeChartData.type}
            grouping={filters.grouping}
          />
        </CardWrapper>
      </div>

      {/* Table */}
      <div className="px-4 md:px-6 md:h-full">
        <StatisticsTable
          statistics={[...statistics].reverse()}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default Statistics;
